import { AxiosResponse } from 'axios'
import { format, parse } from 'date-fns'
import _ from 'lodash'
import { getProfile, postOpenid, getAccessToken } from '../api/auth'
import { postLogout, postRefresh } from '../api/login'
import { imgURL } from '../components/Footer'
import { handlePostMessage } from './postMessage'

interface AnyObject {
  [key: string]: any
}

export function formatCouponDate(useEndYmdt: string) {
  if (!useEndYmdt) return ''
  // "2025-07-15 23:59:59" → "07-15 23:59"
  const date = parse(useEndYmdt, 'yyyy-MM-dd HH:mm:ss', new Date())
  return format(date, 'M월 dd일 HH:mm')
}

/**
 * 가격 천단위 마다 , 찍기
 */
export function plusComma(str: any): any {
  if (str) {
    const text: any = Number(str).toLocaleString()
    return text
  } else if (str === 0) {
    return 0
  }
}

/**
 * 배너 팝업 - 오늘 하루 보지 않기 체크 (1일 1회 체크)
 * @returns boolean - true: 출력, false: 오늘 이미 실행되었음
 */
export const checkTodayBannerPopup: () => Promise<boolean> = async (): Promise<boolean> => {
  const today: string = await getKoreaTime()
  const lastRunDate: string | null = window.localStorage.getItem('lastRunDateExchanging')

  if (lastRunDate === today) {
    return false // 오늘 이미 실행되었음
  } else {
    localStorage.setItem('lastRunDateExchanging', JSON.stringify(today))
    return true // 오늘 처음 실행되거나, 다른 날짜에 마지막으로 실행됨
  }
}

/**
 * 한국 날짜 구하기 UTC => KST
 * @returns 한국날짜 - YYYY-MM-DD
 */
export const getKoreaTime: () => Promise<string> = async (): Promise<string> => {
  const now = new Date()
  const timezoneOffset = now.getTimezoneOffset() * 60 * 1000 // 분 -> 밀리초 변환
  const koreaOffset = 9 * 60 * 60 * 1000 // KST는 UTC+9
  const koreanTime = new Date(now.getTime() + timezoneOffset + koreaOffset)

  return format(koreanTime, 'yyyy-M-d')
}

/**
 * 최근 검색어 - 조회
 * @return {array} recentlySearchWordData - 최근 검색어 데이타
 */
export const getRecentlySearchWord: () => Promise<string | null> = async (): Promise<string | null> => {
  const recentlySearchWordData: string | null = await localStorage.getItem('recentlySearchWord')

  return recentlySearchWordData
}

/**
 * 최근 검색어 - 저장
 * @params {string} word - 검색어
 */
export const setRecentlySearchWord: (word?: any) => Promise<void> = async (word?: any): Promise<void> => {
  const recentlySearchWordData: string | null = await getRecentlySearchWord()

  const wordsArray: any = recentlySearchWordData ? JSON.parse(recentlySearchWordData) : [] // 데이터가 존재하지 않으면 빈 배열을 사용

  const index: any = wordsArray.indexOf(word) // 존재하지 않으면 -1을 반환

  if (index !== -1) {
    wordsArray.splice(index, 1) // 단어가 이미 존재하면, 배열에서 제거
  }
  wordsArray.unshift(word) // 배열의 맨 앞에 단어를 추가

  // 배열의 크기가 특정 크기(예: 10)를 초과하면, 가장 오래된 항목을 제거
  if (wordsArray.length > 10) {
    wordsArray.pop()
  }

  localStorage.setItem('recentlySearchWord', JSON.stringify(wordsArray))
}

/**
 * 최근 검색어 - 삭제
 * @params {string} word - 검색어
 */
export const delRecentlySearchWord: (work?: any) => Promise<void> = async (word?: any): Promise<void> => {
  const recentlySearchWordData: string | null = await getRecentlySearchWord()

  const wordsArray: any = recentlySearchWordData ? JSON.parse(recentlySearchWordData) : [] // 데이터가 존재하지 않으면 빈 배열을 사용

  const index: any = wordsArray.indexOf(word) // 존재하지 않으면 -1을 반환

  if (index !== -1) {
    wordsArray.splice(index, 1) // 단어가 존재하면, 배열에서 제거
  }

  localStorage.setItem('recentlySearchWord', JSON.stringify(wordsArray))
}

/**
 * 최근 검색어 - 전체 삭제
 */
export const delAllRecentlySearchWord: () => void = (): void => {
  localStorage.removeItem('recentlySearchWord')
}

/**
 * 액세스 토큰 > 갱신 여부 확인 - 2시간 지났는지 안지났는지 확인 함수
 * @returns {boolean} - true: 세션 만료 또는 토큰 갱신 필요, false: 갱신 불필요
 */
export const handleAccessToken: (refreshToken?: any, os?: any) => boolean | undefined = (
  refreshToken?: any,
  os?: any
): boolean | undefined => {
  const lastTokenUpdateTimeString: string | null = localStorage.getItem('lastAccessTokenUpdateTime')
  const expiredTime: string | null = sessionStorage.getItem('expiredTime')
  // const expiredTime = "1720418893752";
  const now = new Date()

  // 처음실행하는 경우 (마지막 액세스 토큰 갱신 시간이 없는 경우)
  if (!lastTokenUpdateTimeString) {
    setLastAccessTokenUpdateTime()
    return true
  }
  const lastTokenUpdateTime = new Date(lastTokenUpdateTimeString)
  const twoHoursAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000) // 2시간 => 2시간 안되어서(?), 1시간 59분으로 변경
  const expiredDay = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000) // 3개월

  // 만료90일이 지난 경우 로그아웃 처리
  if (lastTokenUpdateTime < expiredDay) {
    getProfile()
      .then((res: AxiosResponse<any, any>): void => {
        postLogout(Number(res.data.memberId))
          .then(() => {
            window.localStorage.removeItem('aboutRefreshToken')
            window.localStorage.removeItem('aboutAccessToken')
            window.localStorage.removeItem('accessToken')
            window.localStorage.removeItem('lastAccessTokenUpdateTime')
            delPrevScroll()
            window.location.href = '/'
            // window.location.reload()
          })
          .catch((err: any): void => {
            console.log(err)
          })
      })
      .catch((err: any): void => console.log(err))
    return false
  }

  // 앱이고, 앱에서 보내주는 토큰 만료 시간이 지났다면, 리프레시 + 스플래시로 이동
  if (expiredTime) {
    if (new Date(Number(expiredTime)) < new Date(now.getTime())) {
      // 로그아웃이 아니라, 리프레시
      setLastAccessTokenUpdateTime()
      delPrevScroll()
      // moveToLink('aboutfishing://aboutfishing.com/?page=splash');
      handlePostMessage({ actionType: 'moveToLink', params: { url: 'aboutfishing://aboutfishing.com/?page=splash' } })
      // postRefresh(refreshToken, os)
      //   .then((res) => {
      //     // //@ts-ignore
      //     window.localStorage.setItem(
      //       `aboutAccessToken`,
      //       JSON.stringify(res.data.accessToken)
      //     );
      //     postOpenid(res.data.accessToken)
      //       .then((res) => {
      //         window.localStorage.setItem(
      //           `accessToken`,
      //           JSON.stringify(res.data.accessToken)
      //         );

      //       })
      //       .catch((err) => console.log(err));
      //   })
      //   .catch((err) => {
      //     console.log(err);
      //   });
    } else {
      // 만료가 안되었으면 리프레시
      postRefresh(refreshToken, os)
        .then((res: AxiosResponse<any, any>): void => {
          // //@ts-ignore
          window.localStorage.setItem(`aboutAccessToken`, JSON.stringify(res.data.accessToken))
          postOpenid(res.data.accessToken)
            .then((res: AxiosResponse<any, any>): void => {
              window.localStorage.setItem(`accessToken`, JSON.stringify(res.data.accessToken))
              setLastAccessTokenUpdateTime()
              window.location.reload()
            })
            .catch((err: any): void => console.log(err))
        })
        .catch((err: any): void => {
          console.log(err)
        })
    }
  } else {
    // 앱이 아니고, 마지막 갱신 시간으로부터 2시간이 지난 경우
    if (lastTokenUpdateTime < twoHoursAgo) {
      postRefresh(refreshToken, os)
        .then((res: AxiosResponse<any, any>): void => {
          //@ts-ignore
          window.localStorage.setItem(`aboutAccessToken`, JSON.stringify(res.data.accessToken))
          postOpenid(res.data.accessToken)
            .then((res: AxiosResponse<any, any>): void => {
              window.localStorage.setItem(`accessToken`, JSON.stringify(res.data.accessToken))
              setLastAccessTokenUpdateTime()
              window.location.reload()
            })
            .catch((err: any): void => console.log(err))
        })
        .catch((err: any): void => {
          console.log(err)
        })
    }
  }
  // 마지막 갱신 시간이 지나지 않아서 액세스 토큰 갱신 시간을 현재 시간으로 업데이트 => lastAccessTokenUpdateTime 시간은 갱신되지만 accessToken 시간에 공백이 생겨서 주석처리
  // localStorage.setItem("lastAccessTokenUpdateTime", now.toISOString());
}

/**
 * 액세스 토큰 > 액세스 토큰 마지막 갱신 시간 세팅
 */
export const setLastAccessTokenUpdateTime: () => void = (): void => {
  const currentTime = new Date()
  localStorage.setItem('lastAccessTokenUpdateTime', currentTime.toISOString())
}

/**localstorage getitem TypeScript 때문에 사용 */
export function getParsedLocalStorageItem(key: any): any {
  const item: string | null = localStorage.getItem(key)

  if (!item) {
    return null
  }

  try {
    return JSON.parse(item)
  } catch (error) {
    console.error(`Error parsing JSON from localStorage key "${key}":`, error)
    return null
  }
}

/**sessionstorage getitem TypeScript 때문에 사용 */
export function getParsedSessionStorageItem(key?: any): any {
  const item: string | null = sessionStorage.getItem(key)
  if (!item) {
    return null
  }

  try {
    return JSON.parse(item)
  } catch (error) {
    console.error(`Error parsing JSON from localStorage key "${key}":`, error)
    return null
  }
}

export function formatNumberWithTruncation(number?: any): string {
  // 천 단위 이하 숫자 버림
  const truncatedNumber: number = Math.floor(number / 1000) * 1000

  // 포맷팅
  return truncatedNumber.toLocaleString('ko-KR')
}

// 이전 스크롤 위치 조회
export const getPrevScroll: () => string | void | null = (): string | void | null => {
  const val: string | null = window.sessionStorage.getItem('prevScrollVal')
  return val === undefined ? window.sessionStorage.removeItem('prevScrollVal') : val
}

// 이전 스크롤 위치 저장
export const setPrevScroll: (val?: any) => void = (val?: any): void => {
  window.sessionStorage.setItem('prevScrollVal', val)
}

// 이전 스크롤 위치 제거
export const delPrevScroll: () => void = (): void => {
  window.sessionStorage.removeItem('prevScrollVal')
}

// 이전 스크롤 위치 찾아가기
export const prevScrollProcess: () => void = (): void => {
  // const val: string | void | null = getPrevScroll();
  // if (val) {
  //   const scrollPosition = Number(val);
  //   const restoreScroll = (): void => {
  //     // window.scrollTo(0, scrollPosition);
  //     delPrevScroll();
  //   };
  //   requestAnimationFrame(() => {
  //     requestAnimationFrame(restoreScroll); // 두 번 호출하여 렌더링 후에 스크롤 복원
  //   });
  // }
}

// 이전 페이지 저장
export const setCount: (val?: any) => void = (val?: any): void => {
  window.sessionStorage.setItem('totalCount', val)
}

// 이전 페이지 저장
export const setPrevPage: (val?: any) => void = (val?: any): void => {
  window.sessionStorage.setItem('prevPageVal', val)
}

// 이전 페이지 조회
export const getPrevPage: () => string | null = (): string | null => {
  const val: string | null = window.sessionStorage.getItem('prevPageVal')
  return val
}

// 이전 페이지 제거
export const delPrevPage: () => void = (): void => {
  window.sessionStorage.removeItem('prevPageVal')
}

// 이전 페이지 조회
export const getCount: () => string | null = (): string | null => {
  const val: string | null = window.sessionStorage.getItem('totalCount')
  return val
}

// 이전 페이지 제거
export const delCount: () => void = (): void => {
  window.sessionStorage.removeItem('totalCount')
}

// 이전 상품목록 저장
export const setPrevList: (val?: any) => void = (val?: any): void => {
  val && window.sessionStorage.setItem('prevListVal', JSON.stringify(val))
}

// 이전 상품목록 조회
export const getPrevList: () => any = (): any => {
  const val: string | null = window.sessionStorage.getItem('prevListVal')
  return val && JSON.parse(val)
}

// 이전 상품목록 제거
export const delPrevList: () => void = (): void => {
  window.sessionStorage.removeItem('prevListVal')
}

// 모달 팝업 영역 외 영역

// 이미지를 불러오기 전, 또는 이미지가 없을 때 대체 이미지
export const onErrorImg: (e?: any) => void = (e?: any): void => {
  e.target.src = `${imgURL}/no_bg_img.png`
  e.target.style.backgroundImage = `url(${imgURL}/icon_no_image.png)`
  e.target.style.backgroundSize = '36px'
  e.target.style.backgroundPosition = 'center'
  e.target.style.backgroundRepeat = 'no-repeat'
}
// 배경이미지를 불러오기 전, 또는 불러오지 못할 때 대신 호출할 이미지의 경로
export const errorBgImg = `${imgURL}/icon_no_image.png`

//네이버 스크립트 장바구니추가

//공통함수

export const findObjectsWithValue = (arr: Array<AnyObject> = [], valueToFind: string | number) => {
  return _.filter(arr, (obj) => _.some(obj, (value) => Number(value) === Number(valueToFind)))
}

export const extractValuesFromArrayOfObjects = (arr: Array<AnyObject> = []) => {
  // 배열의 각 객체에서 값을 추출하고 평탄화
  return _.flatten(arr.map((obj) => _.values(obj)))
}

export const extractKeysFromArrayOfObjects = (arr: Array<AnyObject> = []) => {
  // 배열의 각 객체에서 키를 추출하고 고유한 키를 결합
  return _.union(...arr.map((obj) => _.keys(obj)))
}

/**
 * 현재 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns {string} 현재 날짜 (YYYY-MM-DD)
 */
export const getFormattedDate = (): string => {
  const today = new Date()

  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export function loadCremaScript() {
  window.cremaAsyncInit = function () {
    console.log('cremaAsyncInit!!!')
    const hasAccessToken = getAccessToken()
    if (hasAccessToken) {
      getProfile().then((res) => {
        console.log('cremaInit 유저 로그인된 경우')
        // window.crema.init(res.data.memberId, null)
        window.crema.init('thecrema', 'thecrema') // 테스트 계정
      })
    } else {
      console.log('cremaInit 유저 로그인이 안된 경우')
      // window.crema.init(null, null)
      window.crema.init('thecrema', 'thecrema') // 테스트 계정
    }
  }
  if (window.crema) window.cremaAsyncInit()
}
